import { MessageChannel } from "@redotech/crdb-enums/shopper-ai/message-channel";
import { MessageSenderType } from "@redotech/crdb-enums/shopper-ai/message-sender-type";
import { ShopperAgentOutboundMessageKind } from "@redotech/crdb-enums/shopper-ai/shopper-agent-outbound-message-kind";
import * as z from "zod";
import { SupervisorDecisionSchema } from "./supervisor-review";

/** A message from either side of a Commerce Agent conversation, or a draft. */
export type ShopperAgentTraceRef = { messageId: string } | { draftId: string };

/**
 * The admin page that shows a trace, and the query parameter that names what
 * to trace. The page opens on the trace unless the link asks for another tab.
 */
export const SHOPPER_AGENT_TRACE_PATH =
  "/marketing/shopper-agent/observability";
export const SHOPPER_AGENT_TRACE_PARAM = "id";

/** A note kept on the conversation, which the shopper never sees. */
export function isInternalNote(channel: string): boolean {
  return channel === MessageChannel.Internalnote;
}

const TracedAsSchema = z.enum(["shopper_message", "reply", "outreach"]);

/**
 * What a message in a Commerce Agent conversation is to the trace, or null
 * when it has none: the agent neither wrote it nor answered it. The agent's
 * label decides, not the sender type, which an outreach does not share with
 * a reply.
 */
export function tracedAs(message: {
  senderType: string;
  /** Null when the agent kept no label for the message. */
  outboundMessageKind: ShopperAgentOutboundMessageKind | null;
  isReply: boolean;
}): z.infer<typeof TracedAsSchema> | null {
  if (message.senderType === MessageSenderType.Customer) {
    return "shopper_message";
  }
  if (message.outboundMessageKind === ShopperAgentOutboundMessageKind.Reply) {
    return "reply";
  }
  if (message.outboundMessageKind !== null) {
    return "outreach";
  }
  // Replies sent before the agent kept a label per message.
  return message.senderType === MessageSenderType.Ai && message.isReply
    ? "reply"
    : null;
}

const ToolResultSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), output: z.unknown() }),
  z.object({ ok: z.literal(false), error: z.string() }),
]);

const StepSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("tool"),
    /** The call's own id, as the LLM thread holds it. */
    callId: z.string(),
    name: z.string(),
    input: z.unknown(),
    /** Null when the call produced no result, as with a handoff. */
    result: ToolResultSchema.nullable(),
  }),
  z.object({ kind: z.literal("drafted"), body: z.string() }),
]);

const ReviewSchema = z.object({
  at: z.date(),
  decision: SupervisorDecisionSchema,
  feedback: z.string(),
  /** The exact draft the supervisor read; null when its log does not hold one. */
  judged: z.string().nullable(),
  /**
   * How many of the run's tool calls had already happened when the supervisor
   * read the draft; null when the review kept no copy of what it was shown.
   */
  toolCallsSeen: z.number().nullable(),
  /**
   * The supervisor's system prompt, merchant guidelines included. Optional so
   * a trace from an older server still parses.
   */
  prompt: z.string().optional(),
});

const OutcomeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("sent"),
    messageId: z.string(),
    /** What went out; null when the message row holds no text. */
    body: z.string().nullable(),
    sentAt: z.date(),
    /** The reviewer who sent it from the queue; null when no person did. */
    sentBy: z.string().nullable(),
    /** The agent's draft, when the reviewer changed it before sending. */
    editedFrom: z.string().nullable(),
  }),
  z.object({ kind: z.literal("waiting"), draftId: z.string() }),
  z.object({ kind: z.literal("not_sent") }),
]);

/** What the shopper wrote; body and time are null when the message row is gone. */
const ShopperMessageSchema = z.object({
  id: z.string(),
  body: z.string().nullable(),
  sentAt: z.date().nullable(),
});

/** One go at writing one message. */
const AttemptSchema = z.object({
  kind: z.enum(ShopperAgentOutboundMessageKind),
  /** Auto-send, or a suggestion for a person; null when the log does not say. */
  mode: z.enum(["autopilot", "copilot"]).nullable(),
  startedBy: z.union([
    z.object({ shopperMessages: z.array(ShopperMessageSchema) }),
    z.object({
      triggerKey: z.string().nullable(),
      /**
       * The campaign or automation step that sent the outreach. Optional so a
       * trace from an older server still parses.
       */
      campaignId: z.string().nullable().optional(),
      automationId: z.string().nullable().optional(),
      automationStepId: z.string().nullable().optional(),
    }),
  ]),
  /** In LLM thread order; null when the steps could not be tied to this attempt. */
  steps: z.array(StepSchema).nullable(),
  /**
   * An outreach writer's system prompt and opening message; a reply log keeps
   * one prompt for many attempts. Optional so older servers' traces parse.
   */
  writerPrompt: z
    .object({ system: z.string(), opening: z.string() })
    .optional(),
  /** Every supervisor verdict, oldest first. */
  reviews: z.array(ReviewSchema),
  outcome: OutcomeSchema,
  llmThreadId: z.string().nullable(),
  runId: z.string().nullable(),
});

export const ShopperAgentTraceSchema = z.object({
  /** A replaced draft never went out; its attempt is the one whose draft took its place. */
  asked: z.enum([
    "agent_message",
    "shopper_message",
    "draft",
    "replaced_draft",
  ]),
  teamId: z.string(),
  channelThreadId: z.string(),
  channel: z.string(),
  /**
   * Agent message or draft: the attempt that wrote it. Shopper message:
   * every attempt that answered it; empty when none is recorded.
   */
  attempts: z.array(AttemptSchema),
});

export type ShopperAgentTrace = z.infer<typeof ShopperAgentTraceSchema>;

/** One page of a conversation, for picking what to trace. */
export const ShopperAgentTraceMessagesSchema = z.object({
  /** The agent's unsent drafts, newest first; only on the newest page. */
  waitingDrafts: z.array(
    z.object({
      id: z.string(),
      body: z.string().nullable(),
      createdAt: z.date(),
    }),
  ),
  /** Newest first. */
  messages: z.array(
    z.object({
      id: z.string(),
      senderType: z.string(),
      /** True for a note the shopper never saw. */
      internalNote: z.boolean(),
      body: z.string().nullable(),
      sentAt: z.date(),
      /** Null for what the agent neither wrote nor answered. */
      tracedAs: TracedAsSchema.nullable(),
      /**
       * True for a shopper message the agent replied to or has a draft
       * waiting for: its trace is the one that reply or draft already shows.
       */
      answered: z.boolean(),
      /**
       * True for a shopper message the agent worked on before the next one came,
       * sent or not. Optional so a page from an older server still parses.
       */
      followedByAgentWork: z.boolean().optional(),
    }),
  ),
  /** The message to page from next; null when nothing older is left. */
  next: z.string().nullable(),
});

export type ShopperAgentTraceMessages = z.infer<
  typeof ShopperAgentTraceMessagesSchema
>;

/**
 * The ids of the listed messages whose own trace is worth opening. A shopper
 * message is left out when the agent answered it, or answered another message
 * of the same run: several shopper messages in a row get one reply, and that
 * reply's trace covers them all. Anything between two shopper messages ends
 * the run, as does the agent's own work, which a silent turn leaves unlisted.
 */
export function messagesOfferingTrace(
  oldestFirst: ShopperAgentTraceMessages["messages"],
): Set<string> {
  const offering = new Set<string>();
  let run: string[] = [];
  let runAnswered = false;
  const endRun = () => {
    if (!runAnswered) {
      for (const id of run) {
        offering.add(id);
      }
    }
    run = [];
    runAnswered = false;
  };
  for (const message of oldestFirst) {
    if (message.tracedAs === "shopper_message") {
      run.push(message.id);
      runAnswered ||= message.answered;
      if (message.followedByAgentWork) {
        endRun();
      }
      continue;
    }
    endRun();
    if (message.tracedAs !== null) {
      offering.add(message.id);
    }
  }
  endRun();
  return offering;
}
export type ShopperAgentTraceAttempt = ShopperAgentTrace["attempts"][number];
export type ShopperAgentTraceStep = NonNullable<
  ShopperAgentTraceAttempt["steps"]
>[number];
export type ShopperAgentTraceReview =
  ShopperAgentTraceAttempt["reviews"][number];
export type ShopperAgentTraceShopperMessage = z.infer<
  typeof ShopperMessageSchema
>;
